import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Tile,
  TileGroup,
  Typography,
} from '@one-thd/sui-atomic-components';
import { DateCalendar } from '@one-thd/sui-date-pickers';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { DateTime } from 'luxon';
import React, { ChangeEvent, useState } from 'react';
import { FeatureSwitches } from '../../../constants/feature-switches';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { TDateSelectValues } from '../../../models/common';
import { IPHRequest } from '../../../models/purchase-history';
import { DateRanges } from '../../../utils/DateRanges';
import { getWindow } from '../../../utils/general';
import { OrderFilterInput } from './OrderFilterInput';
import {
  getEffectiveFilterDateRange,
  getFilterDetailsFromSession,
} from '../../../utils/purchase-history/common';

interface IOrderFilterDrawer {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isOpen: boolean) => void;
  dateFilterDot: { date: boolean; user: boolean };
  setDateFilterDot: React.Dispatch<
    React.SetStateAction<{
      date: boolean;
      user: boolean;
    }>
  >;
}

const dateTileOptions = [
  { value: 'last30Days', label: 'Last 30 Days' },
  { value: 'last90Days', label: 'Last 90 Days' },
  { value: 'last6Months', label: 'Last 6 Months' },
  { value: 'last25Months', label: 'Last 25 Months' },
  { value: 'thisYear', label: new Date().getFullYear().toString() },
  { value: 'lastYear', label: (new Date().getFullYear() - 1).toString() },
  { value: 'twoYearsPrior', label: (new Date().getFullYear() - 2).toString() },
  {
    value: 'threeYearsPrior',
    label: (new Date().getFullYear() - 3).toString(),
  },
  { value: 'fourYearsPrior', label: (new Date().getFullYear() - 4).toString() },
  { value: 'fiveYearsPrior', label: (new Date().getFullYear() - 5).toString() },
  { value: 'sixYearsPrior', label: (new Date().getFullYear() - 6).toString() },
];

const userTileOptions = (username: string) => [
  { value: 'allOrders', label: 'Everyone' },
  { value: 'myOrders', label: `${username ?? ''} (Just Me)` },
];

const parseDateToString = (date: string | null) => {
  const [year, month, day] = date ? date.split('-').map(Number) : [0, 0, 0];
  const monthString = month < 10 ? `0${month}` : `${month}`;
  const dayString = day < 10 ? `0${day}` : `${day}`;
  return `${monthString}/${dayString}/${year}`;
};

export const OrderFilterDrawer = ({
  isDrawerOpen,
  setIsDrawerOpen,
  dateFilterDot,
  setDateFilterDot,
}: IOrderFilterDrawer) => {
  const { username } = useThdCustomer();
  const {
    dateSelectorValue,
    pHRequest,
    setDateSelectorValue,
    setPHRequest,
    setAlertState,
  } = usePurchaseHistoryContext();

  const sessionPHFilter = getFilterDetailsFromSession();

  const [filterSettings, setFilterSettings] = useState({
    userView: pHRequest?.resultsFilter || 'allOrders',
    customDateUsed: false,
    showStartCalendar: false,
    showEndCalendar: false,
    invalidStartDate: false,
    invalidEndDate: false,
  });
  const [dateSettings, setDateSettings] = useState<{
    selectedDate: TDateSelectValues | null;
    startDate: DateTime | string | null;
    endDate: DateTime | string | null;
  }>(() => {
    const { startDate, endDate } = getEffectiveFilterDateRange(
      pHRequest,
      sessionPHFilter,
    );
    return {
      selectedDate: sessionPHFilter?.selectedDate ?? dateSelectorValue,
      startDate: startDate ?? null,
      endDate: endDate ?? null,
    };
  });

  const maxDate = DateTime.now().startOf('day').toString();
  const minDate = DateTime.now()
    .minus({ months: 84 })
    .startOf('day')
    .toString();

  const closeFilterDrawer = () => {
    setIsDrawerOpen(false);
  };

  const onDateSelect = (
    _event: React.SyntheticEvent<Element, Event>,
    date: string,
  ): void => {
    if (date === dateSettings.selectedDate) {
      return;
    }
    if (date) {
      setFilterSettings((prevState) => ({
        ...prevState,
        customDateUsed: false,
      }));
      setDateSettings((prevState) => ({
        ...prevState,
        selectedDate: date as TDateSelectValues,
      }));

      const startDateStr =
        DateRanges?.[(date as keyof typeof DateRanges) ?? '']?.startDate || '';
      const endDateStr =
        DateRanges?.[(date as keyof typeof DateRanges) ?? '']?.endDate || '';

      const newStartDate = parseDateToString(startDateStr);
      const newEndDate = parseDateToString(endDateStr);

      setDateSettings((prevState) => ({
        ...prevState,
        startDate: startDateStr ? newStartDate : null,
        endDate: endDateStr ? newEndDate : null,
      }));
    }
  };

  const handleUserViewChange = () => {
    setPHRequest((prevState: IPHRequest) => {
      return {
        ...prevState,
        resultsFilter: filterSettings.userView ?? undefined,
        pageNumber: 1,
      };
    });
    getWindow()?.sessionStorage.setItem(
      'PHFilter',
      JSON.stringify({ ...dateSettings, ...filterSettings }),
    );
    setDateFilterDot((prevState) => ({ ...prevState, user: true }));
    closeFilterDrawer();
  };

  const modifyDateFormat = (
    startDate: string | DateTime | null,
    endDate: string | DateTime | null,
  ) => {
    const modifiedStartDate = startDate?.toString().split('/');
    const modifiedEndDate = endDate?.toString().split('/');

    const newStartDate =
      modifiedStartDate && modifiedStartDate.length === 3
        ? `${modifiedStartDate[2]}-${modifiedStartDate[0]}-${modifiedStartDate[1]}`
        : '';
    const newEndDate =
      modifiedEndDate && modifiedEndDate.length === 3
        ? `${modifiedEndDate[2]}-${modifiedEndDate[0]}-${modifiedEndDate[1]}`
        : '';

    return [newStartDate, newEndDate];
  };

  const handleDateViewChange = () => {
    const [newStartDate, newEndDate] = modifyDateFormat(
      dateSettings.startDate,
      dateSettings.endDate,
    );

    setDateSelectorValue(
      (dateSettings.selectedDate as TDateSelectValues) ||
        (FeatureSwitches.enableLSSCPS25MonthsDefault
          ? ('last25Months' as TDateSelectValues)
          : ('last6Months' as TDateSelectValues)),
    );
    setPHRequest((prevState) => ({
      ...prevState,
      startDate: dateSettings.startDate ? newStartDate ?? '' : '',
      endDate: dateSettings.endDate ? newEndDate ?? '' : '',
      pageNumber: 1,
    }));

    getWindow()?.sessionStorage.setItem(
      'PHFilter',
      JSON.stringify({ ...dateSettings, ...filterSettings }),
    );

    setDateFilterDot((prevState) => ({ ...prevState, date: true }));
    closeFilterDrawer();
  };

  const applyDateDisabled = () => {
    if (filterSettings.showStartCalendar) {
      return !dateSettings.startDate;
    }
    if (filterSettings.showEndCalendar) {
      return !dateSettings.endDate;
    }
    return false;
  };

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={closeFilterDrawer}
      style={{ zIndex: 20000000010 }}
    >
      {filterSettings.showStartCalendar || filterSettings.showEndCalendar ? (
        <DrawerHeader
          title="Custom Range"
          onBack={() => {
            if (filterSettings.showStartCalendar) {
              setFilterSettings((prevState) => ({
                ...prevState,
                showStartCalendar: false,
              }));
            } else {
              setFilterSettings((prevState) => ({
                ...prevState,
                showEndCalendar: false,
              }));
            }
          }}
          onClose={closeFilterDrawer}
        />
      ) : (
        <DrawerHeader title="Filter" onClose={closeFilterDrawer} />
      )}
      <DrawerBody>
        {filterSettings.showStartCalendar || filterSettings.showEndCalendar ? (
          <div>
            <DateCalendar
              minDate={minDate}
              maxDate={maxDate}
              value={
                filterSettings.showStartCalendar
                  ? (dateSettings.startDate as string) || ''
                  : (dateSettings.endDate as string) || ''
              }
              onChange={(_event, value) => {
                setFilterSettings((prevState) => ({
                  ...prevState,
                  customDateUsed: true,
                }));

                const modifyDate = value.toString().split('T')[0];
                const newDate = parseDateToString(modifyDate);

                if (filterSettings.showStartCalendar) {
                  setDateSettings((prevState) => ({
                    ...prevState,
                    startDate: newDate,
                    selectedDate: 'custom',
                  }));
                } else if (filterSettings.showEndCalendar) {
                  setDateSettings((prevState) => ({
                    ...prevState,
                    endDate: newDate,
                    selectedDate: 'custom',
                  }));
                }
              }}
            />
          </div>
        ) : (
          <>
            <div className="sui-relative sui-border-b-1 sui-border-solid sui-border-strong">
              {dateFilterDot.user && (
                <div
                  style={{
                    position: 'absolute',
                    top: '30px',
                    left: '40px',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: 'rgb(249,99,2)',
                    marginLeft: '8px',
                    zIndex: 100,
                  }}
                />
              )}
              <Accordion noBorders defaultExpanded>
                <AccordionHeader aria-controls="accordion1-content">
                  User
                </AccordionHeader>
                <AccordionBody>
                  <div className="sui-mb-[12px]">
                    <TileGroup
                      value={filterSettings.userView}
                      onChange={(
                        _event: ChangeEvent<HTMLInputElement>,
                        value: string,
                      ) => {
                        if (value) {
                          setFilterSettings((prevState) => ({
                            ...prevState,
                            userView: value,
                          }));
                        }
                      }}
                      aria-label="date filter"
                      orientation="vertical"
                    >
                      {userTileOptions(username).map((option) => (
                        <Tile
                          key={option.value}
                          value={option.value}
                          aria-label="grid view"
                        >
                          <Typography className="sui-mr-auto">
                            {option.label}
                          </Typography>
                        </Tile>
                      ))}
                    </TileGroup>
                  </div>
                </AccordionBody>
              </Accordion>
            </div>
            <div className="sui-relative sui-pt-[14px]">
              {dateFilterDot.date && (
                <div
                  style={{
                    position: 'absolute',
                    top: '17px',
                    left: '40px',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: 'rgb(249,99,2)',
                    marginLeft: '8px',
                    zIndex: 100,
                  }}
                />
              )}
              <Accordion noBorders defaultExpanded>
                <AccordionHeader aria-controls="accordion1-content">
                  Date
                </AccordionHeader>
                <AccordionBody>
                  <div className="sui-mb-[20px]">
                    <Typography weight="bold" className="sui-mb-[4px]">
                      Enter a custom range:
                    </Typography>
                    <div className="sui-flex sui-flex-col sui-gap-[12px] sui-w-full sui-items-center">
                      <OrderFilterInput
                        name="Start date"
                        dateType="startDate"
                        dateSettings={dateSettings}
                        setDateSettings={setDateSettings}
                        setFilterSettings={setFilterSettings}
                      />
                      <OrderFilterInput
                        name="End date"
                        dateType="endDate"
                        dateSettings={dateSettings}
                        setDateSettings={setDateSettings}
                        setFilterSettings={setFilterSettings}
                      />
                    </div>
                  </div>
                  <div className="sui-mb-[12px]">
                    <Typography weight="bold" style={{ marginBottom: '8px' }}>
                      Select a range:
                    </Typography>
                    <TileGroup
                      value={dateSettings.selectedDate}
                      onChange={onDateSelect}
                      aria-label="date filter"
                      orientation="vertical"
                    >
                      {dateTileOptions.map((option, key) => {
                        if (
                          FeatureSwitches.enableLSSCPS25MonthsDefault ===
                            false ||
                          FeatureSwitches.enableLSSCPS25MonthsDefault === null
                        ) {
                          if (option.value === 'last25Months') {
                            return null;
                          }
                        }
                        return (
                          <Tile
                            key={key}
                            value={option.value}
                            aria-label="list view"
                          >
                            <Typography className="sui-mr-auto">
                              {option.label}
                            </Typography>
                          </Tile>
                        );
                      })}
                    </TileGroup>
                  </div>
                </AccordionBody>
              </Accordion>
            </div>
          </>
        )}
      </DrawerBody>
      {filterSettings.showStartCalendar || filterSettings.showEndCalendar ? (
        <DrawerFooter>
          <Button
            variant="primary"
            fullWidth
            disabled={applyDateDisabled()}
            onClick={() =>
              setFilterSettings((prevState) => ({
                ...prevState,
                showStartCalendar: false,
                showEndCalendar: false,
              }))
            }
          >
            Apply
          </Button>
        </DrawerFooter>
      ) : (
        <DrawerFooter>
          <Button
            variant="primary"
            fullWidth
            disabled={
              filterSettings.userView === pHRequest.resultsFilter &&
              (!dateSettings.startDate || !dateSettings.endDate)
            }
            onClick={() => {
              if (filterSettings.userView !== pHRequest.resultsFilter) {
                handleUserViewChange();
                return;
              }

              setAlertState(null);
              handleDateViewChange();
            }}
          >
            View Results
          </Button>
        </DrawerFooter>
      )}
    </Drawer>
  );
};
